import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "list",
    "sentinel",
    "spinner",
    "results",
    "loading",
    "tabDescription",
    "tagsTab",
    "tagsPane",
    "tagsContent",
    "tagsList",
    "noTagsMessage",
    "newTagInput",
    "tagError",
    "deleteTagName",
    "shareTagName",
    "shareCurrentUsers",
    "shareSearch",
    "shareResults",
  ];
  static values = {
    currentUrl: String,
    nextUrl: String,
    tagsUrl: String,
    confirmPrivate: Boolean,
    privateAccount: Boolean,
  };

  connect() {
    this.modal = new bootstrap.Modal(
      document.getElementById("bingeEpisodeModal"),
    );
    this.form = document.getElementById("bingeEpisodeForm");
    this.watchedCheckbox = document.getElementById("bingeWatchedCheckbox");
    this.modalTitle = document.getElementById("bingeEpisodeModalLabel");
    this.modalEpisodeName = document.getElementById("bingeEpisodeName");
    this.modalEpisodeBadge = document.getElementById("bingeEpisodeBadge");
    this.saveBtn = document.getElementById("bingeEpisodeSaveBtn");
    this.starRatingContainer = document.getElementById("bingeStarRating");
    this.currentItem = null;
    this.pendingUpdate = null;
    this.tagNames = JSON.parse(localStorage.getItem("binge_tag_names") || "{}");
    this.activeTagFilters = new Set(Object.keys(this.tagNames));
    this.activeTab = localStorage.getItem("binge_tab") || "current";
    this.activeSort = localStorage.getItem("binge_sort") || "date";
    this.tabCache = {};
    this.loading = false;

    this.pendingFormData = null;
    this.pendingFormAction = null;
    this.currentFetch = null;
    this.tagsLoaded = false;
    this.pendingDeleteTagId = null;
    this.currentShareTagId = null;
    this.currentShareBtn = null;
    const tagDeleteModalEl = document.getElementById("bingeTagDeleteModal");
    if (tagDeleteModalEl)
      this.tagDeleteModal = new bootstrap.Modal(tagDeleteModalEl);
    const tagShareModalEl = document.getElementById("bingeTagShareModal");
    if (tagShareModalEl)
      this.tagShareModal = new bootstrap.Modal(tagShareModalEl);

    this.boundSharedWatchCompleted = this.handleSharedWatchCompleted.bind(this)
    document.addEventListener("shared-watch:completed", this.boundSharedWatchCompleted)

    this.setupStarRating();
    this.setupClickHandlers();
    this.setupTagFilter();
    this.setupFormSubmit();
    this.setupPrivateModal();
    this.setupTabButtons();
    this.setupSortButtons();
    if (this.activeTab === "tags") {
      this.#loadTags();
    } else {
      this.loadTabContent(this.activeTab);
    }

    document
      .getElementById("bingeEpisodeModal")
      .addEventListener("hidden.bs.modal", () => {
        if (this.pendingUpdate) {
          this.updateCurrentItem(this.pendingUpdate);
          this.pendingUpdate = null;
        }
      });
  }

  setupTagFilter() {
    this.filterIndicator = document.getElementById("binge-tag-filter");
    this.tagFilterCheckboxes = document.querySelectorAll(
      "[data-binge-tag-filter-id]",
    );

    const validIds = new Set(
      Array.from(this.tagFilterCheckboxes).map(
        (cb) => cb.dataset.bingeTagFilterId,
      ),
    );
    [...this.activeTagFilters].forEach((tagId) => {
      if (!validIds.has(tagId)) {
        this.activeTagFilters.delete(tagId);
        delete this.tagNames[tagId];
      }
    });
    this.tagFilterCheckboxes.forEach((cb) => {
      const tagId = cb.dataset.bingeTagFilterId;
      cb.checked = this.activeTagFilters.has(tagId);
      if (cb.checked && !this.tagNames[tagId]) {
        this.tagNames[tagId] = cb.dataset.bingeTagFilterName;
      }
    });
    this.saveTagFilters();

    this.element.addEventListener("click", (event) => {
      const badge = event.target.closest(".binge-tag-badge");
      if (!badge) return;
      event.stopPropagation();

      const tagId = badge.dataset.tagId;
      if (this.activeTagFilters.has(tagId)) return;
      this.tagNames[tagId] = badge.dataset.tagName;
      this.activeTagFilters.add(tagId);
      this.saveTagFilters();
      this.applyFiltersToCurrentTab();
    });
  }

  toggleTagFilter(event) {
    const checkbox = event.currentTarget;
    const tagId = checkbox.dataset.bingeTagFilterId;
    if (checkbox.checked) {
      this.activeTagFilters.add(tagId);
      this.tagNames[tagId] = checkbox.dataset.bingeTagFilterName;
    } else {
      this.activeTagFilters.delete(tagId);
      delete this.tagNames[tagId];
    }
    this.saveTagFilters();
    this.applyFiltersToCurrentTab();
  }

  applyFiltersToCurrentTab() {
    this.tabCache = {};
    this.loadTabContent(this.activeTab);
  }

  buildTabUrl(tab, page = null) {
    const baseUrl =
      tab === "current" ? this.currentUrlValue : this.nextUrlValue;
    const queryParams = new URLSearchParams();
    queryParams.set("sort", this.activeSort);
    if (page) queryParams.set("page", page);
    this.activeTagFilters.forEach((tagId) => {
      queryParams.append("tag_ids[]", tagId);
    });
    return `${baseUrl}?${queryParams.toString()}`;
  }

  setupTabButtons() {
    const isCurrent = this.activeTab === "current";
    const isNext = this.activeTab === "next";
    const isTags = this.activeTab === "tags";
    document
      .getElementById("binge-tab-current")
      ?.classList.toggle("active", isCurrent);
    document
      .getElementById("binge-tab-next")
      ?.classList.toggle("active", isNext);
    document
      .getElementById("binge-tab-tags")
      ?.classList.toggle("active", isTags);
    if (this.hasTabDescriptionTarget) {
      if (isTags) {
        this.tabDescriptionTarget.textContent = "";
      } else {
        this.tabDescriptionTarget.textContent = isCurrent
          ? "Content you are currently binging"
          : "Content you have not started binging yet";
      }
    }
    if (this.hasResultsTarget)
      this.resultsTarget.classList.toggle("d-none", isTags);
    if (this.hasTagsPaneTarget)
      this.tagsPaneTarget.classList.toggle("d-none", !isTags);
  }

  setupSortButtons() {
    const isDate = this.activeSort === "date";
    document
      .getElementById("binge-sort-date")
      ?.classList.toggle("active", isDate);
    document
      .getElementById("binge-sort-alpha")
      ?.classList.toggle("active", !isDate);
  }

  sortDate() {
    if (this.activeSort === "date") return;
    this.activeSort = "date";
    localStorage.setItem("binge_sort", "date");
    this.tabCache = {};
    this.setupSortButtons();
    this.loadTabContent(this.activeTab);
  }

  sortAlpha() {
    if (this.activeSort === "alpha") return;
    this.activeSort = "alpha";
    localStorage.setItem("binge_sort", "alpha");
    this.tabCache = {};
    this.setupSortButtons();
    this.loadTabContent(this.activeTab);
  }

  showCurrent() {
    if (this.activeTab === "current") return;
    const previousTab = this.activeTab;
    this.activeTab = "current";
    localStorage.setItem("binge_tab", "current");
    this.setupTabButtons();
    if (previousTab !== "tags") this.loadTabContent("current", previousTab);
    else this.loadTabContent("current");
  }

  showNext() {
    if (this.activeTab === "next") return;
    const previousTab = this.activeTab;
    this.activeTab = "next";
    localStorage.setItem("binge_tab", "next");
    this.setupTabButtons();
    if (previousTab !== "tags") this.loadTabContent("next", previousTab);
    else this.loadTabContent("next");
  }

  showTags() {
    if (this.activeTab === "tags") return;
    const previousTab = this.activeTab;
    if (previousTab !== "tags") this.saveTab(previousTab);
    this.activeTab = "tags";
    localStorage.setItem("binge_tab", "tags");
    this.setupTabButtons();
    this.#loadTags();
  }

  saveTab(tab) {
    const nextPage = this.hasSentinelTarget
      ? this.sentinelTarget.dataset.nextPage
      : null;
    this.tabCache[tab] = { cards: [...this.listTarget.children], nextPage };
  }

  clearTabUI() {
    this.disconnectObserver();
    if (this.hasSentinelTarget) this.sentinelTarget.remove();
    if (this.hasSpinnerTarget) this.spinnerTarget.remove();
    this.listTarget.innerHTML = "";
  }

  restoreTab(tab) {
    const cache = this.tabCache[tab];
    cache.cards.forEach((card) => this.listTarget.appendChild(card));

    if (cache.nextPage) {
      const sentinel = document.createElement("div");
      sentinel.dataset.bingeTarget = "sentinel";
      sentinel.dataset.nextPage = cache.nextPage;
      this.resultsTarget.appendChild(sentinel);

      const spinner = document.createElement("div");
      spinner.className = "flow-loading text-center py-4";
      spinner.dataset.bingeTarget = "spinner";
      spinner.innerHTML =
        '<div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>';
      this.resultsTarget.insertBefore(spinner, sentinel);

      this.observeSentinel();
    }
  }

  loadTabContent(tab, previousTab = null) {
    if (previousTab !== null) this.saveTab(previousTab);
    this.clearTabUI();

    if (this.tabCache[tab]) {
      this.restoreTab(tab);
      this.updateFilter();
      return;
    }

    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("d-none");

    if (this.currentFetch) {
      this.currentFetch.abort();
      this.currentFetch = null;
    }

    const controller = new AbortController();
    this.currentFetch = controller;

    fetch(this.buildTabUrl(tab), {
      headers: { "X-Requested-With": "XMLHttpRequest" },
      signal: controller.signal,
    })
      .then((response) => response.text())
      .then((html) => {
        const temp = document.createElement("div");
        temp.innerHTML = html;

        const cards = temp.querySelectorAll(".next-item-card");
        cards.forEach((card) => this.listTarget.appendChild(card));

        const match = html.match(/<!-- next_page:(\d+) -->/);
        if (match) {
          const sentinel = document.createElement("div");
          sentinel.dataset.bingeTarget = "sentinel";
          sentinel.dataset.nextPage = match[1];
          this.resultsTarget.appendChild(sentinel);

          const spinner = document.createElement("div");
          spinner.className = "flow-loading text-center py-4";
          spinner.dataset.bingeTarget = "spinner";
          spinner.innerHTML =
            '<div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>';
          this.resultsTarget.insertBefore(spinner, sentinel);

          this.observeSentinel();
        }

        this.tabCache[tab] = {
          cards: [...this.listTarget.children],
          nextPage: match ? match[1] : null,
        };
        this.updateFilter();
      })
      .catch((error) => {
        if (error.name !== "AbortError") throw error;
      })
      .finally(() => {
        if (this.currentFetch === controller) this.currentFetch = null;
        if (this.hasLoadingTarget) this.loadingTarget.classList.add("d-none");
      });
  }

  updateFilter() {
    const filters = this.activeTagFilters;

    this.element.querySelectorAll(".binge-tag-badge").forEach((badge) => {
      badge.classList.toggle("active", filters.has(badge.dataset.tagId));
    });

    if (this.tagFilterCheckboxes) {
      this.tagFilterCheckboxes.forEach((checkbox) => {
        checkbox.checked = filters.has(checkbox.dataset.bingeTagFilterId);
      });
    }

    if (this.filterIndicator) {
      if (filters.size === 0) {
        this.filterIndicator.classList.add("d-none");
        this.filterIndicator.innerHTML = "";
      } else {
        const chips = [...filters]
          .map((tagId) => {
            const tagName = (this.tagNames[tagId] || "")
              .replace(/\s*\(from [^)]+\)/, "")
              .trim();
            return tagName
              ? `<span class="badge content-user-tag-badge binge-filter-chip me-1">${tagName} <button type="button" class="btn-close btn-close-white ms-1 binge-filter-chip-close" data-clear-tag-id="${tagId}"></button></span>`
              : "";
          })
          .filter(Boolean)
          .join("");
        this.filterIndicator.innerHTML = `<span class="text-muted small me-1">Filtered by:</span>${chips}`;
        this.filterIndicator.classList.remove("d-none");
        this.filterIndicator
          .querySelectorAll("[data-clear-tag-id]")
          .forEach((closeButton) => {
            closeButton.addEventListener("click", (event) => {
              event.stopPropagation();
              const clearId = closeButton.dataset.clearTagId;
              this.activeTagFilters.delete(clearId);
              delete this.tagNames[clearId];
              this.saveTagFilters();
              this.applyFiltersToCurrentTab();
            });
          });
      }
    }
  }

  saveTagFilters() {
    localStorage.setItem("binge_tag_names", JSON.stringify(this.tagNames));
  }

  clearTagFilter() {
    this.activeTagFilters.clear();
    this.tagNames = {};
    this.saveTagFilters();
    if (this.tagFilterCheckboxes) {
      this.tagFilterCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });
    }
    this.applyFiltersToCurrentTab();
  }

  setupClickHandlers() {
    this.element.addEventListener("click", (e) => {
      if (e.target.closest("a")) return;
      if (e.target.closest(".binge-tag-badge")) return;

      const sharedWatchBtn = e.target.closest(".shared-watch-btn");
      if (sharedWatchBtn) {
        const card = sharedWatchBtn.closest(".next-item-card");
        this.currentItem = card;
        document.dispatchEvent(new CustomEvent("shared-watch:open", {
          detail: {
            episodeSlug: card.dataset.episodeSlug,
            contentName: card.dataset.contentName,
            seasonNumber: card.dataset.seasonNumber,
            episodeNumber: card.dataset.episodeNumber,
            episodeName: card.dataset.episodeName,
            tagIds: card.dataset.tagIds ? card.dataset.tagIds.split(",").filter(Boolean) : []
          }
        }));
        return;
      }

      const watchBtn = e.target.closest(".binge-watch-btn");
      const card = watchBtn
        ? watchBtn.closest(".next-item-card")
        : e.target.closest(".binge-episode-clickable");
      if (!card) return;

      this.currentItem = card;
      this.modalTitle.textContent = card.dataset.contentName;
      this.modalEpisodeBadge.innerHTML =
        '<i class="bi bi-collection-play me-1"></i>' +
        "S" +
        String(card.dataset.seasonNumber).padStart(2, "0") +
        " E" +
        String(card.dataset.episodeNumber).padStart(2, "0");
      this.modalEpisodeName.textContent = card.dataset.episodeName;
      this.form.action = "/episode/" + card.dataset.episodeSlug + "/update/";
      this.watchedCheckbox.checked = card.dataset.episodeWatched === "true";
      this.setRating(card.dataset.episodeRating);
      this.modal.show();
    });
  }

  setupStarRating() {
    const starContainers =
      this.starRatingContainer.querySelectorAll(".star-container");

    const updateStarDisplay = (value) => {
      this.starRatingContainer.classList.add("rating-active");
      starContainers.forEach((container, index) => {
        const starNumber = index + 1;
        container.classList.remove("highlighted", "half-highlighted");
        if (value >= starNumber) {
          container.classList.add("highlighted");
        } else if (value >= starNumber - 0.5) {
          container.classList.add("half-highlighted");
        }
      });
    };

    const clearStarDisplay = () => {
      this.starRatingContainer.classList.remove("rating-active");
      starContainers.forEach((container) => {
        container.classList.remove("highlighted", "half-highlighted");
      });
    };

    this.starRatingContainer
      .querySelectorAll("input[type='radio']")
      .forEach((radio) => {
        radio.addEventListener("change", () => {
          if (radio.checked && radio.value) {
            updateStarDisplay(parseFloat(radio.value));
            if (this.watchedCheckbox && !this.watchedCheckbox.checked) {
              this.watchedCheckbox.checked = true;
            }
          }
        });
      });

    starContainers.forEach((container) => {
      const halfLabel = container.querySelector(".star-half-label");
      const fullLabel = container.querySelector(".star-full-label");

      container.addEventListener("mouseenter", () => {
        if (fullLabel) updateStarDisplay(parseFloat(fullLabel.dataset.value));
      });

      if (halfLabel) {
        halfLabel.addEventListener("mouseenter", (e) => {
          e.stopPropagation();
          updateStarDisplay(parseFloat(halfLabel.dataset.value));
        });
      }

      if (fullLabel) {
        fullLabel.addEventListener("mouseenter", (e) => {
          e.stopPropagation();
          updateStarDisplay(parseFloat(fullLabel.dataset.value));
        });
      }
    });

    this.starRatingContainer.addEventListener("mouseleave", () => {
      const checkedRadio = this.starRatingContainer.querySelector(
        "input[type='radio']:checked",
      );
      if (checkedRadio && checkedRadio.value) {
        updateStarDisplay(parseFloat(checkedRadio.value));
      } else {
        clearStarDisplay();
      }
    });

    const clearBtn = this.starRatingContainer.querySelector("[data-clear-rating]");
    if (clearBtn) {
      clearBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.starRatingContainer
          .querySelectorAll("input[type='radio']")
          .forEach((r) => {
            r.checked = false;
          });
        const noneRadio = document.getElementById("binge-rating-none");
        if (noneRadio) noneRadio.checked = true;
        clearStarDisplay();
      });
    }
  }

  setRating(rating) {
    this.starRatingContainer
      .querySelectorAll("input[type='radio']")
      .forEach((radio) => {
        radio.checked = false;
      });

    if (rating) {
      const ratingNum = parseFloat(rating);
      const normalizedRating =
        ratingNum % 1 === 0
          ? Math.floor(ratingNum).toString()
          : ratingNum.toString();
      const ratingStr = normalizedRating.replace(".", "-");
      const ratingRadio = document.getElementById("binge-rating-" + ratingStr);
      if (ratingRadio) {
        ratingRadio.checked = true;
        this.starRatingContainer.classList.add("rating-active");
        const starContainers =
          this.starRatingContainer.querySelectorAll(".star-container");
        starContainers.forEach((container, index) => {
          const starNumber = index + 1;
          container.classList.remove("highlighted", "half-highlighted");
          if (ratingNum >= starNumber) {
            container.classList.add("highlighted");
          } else if (ratingNum >= starNumber - 0.5) {
            container.classList.add("half-highlighted");
          }
        });
      }
    } else {
      const noneRadio = document.getElementById("binge-rating-none");
      if (noneRadio) noneRadio.checked = true;
      this.starRatingContainer.classList.remove("rating-active");
      this.starRatingContainer
        .querySelectorAll(".star-container")
        .forEach((container) => {
          container.classList.remove("highlighted", "half-highlighted");
        });
    }
  }

  setupFormSubmit() {
    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      const formData = new FormData(this.form);
      const rating = formData.get("rating");

      if (rating && this.confirmPrivateValue && !this.privateAccountValue) {
        this.pendingFormData = formData;
        this.pendingFormAction = this.form.action;
        this.modal.hide();
        this.privateRatingModal.show();
        return;
      }

      this.submitBingeForm(formData, this.form.action, null);
    });
  }

  setupPrivateModal() {
    const privateRatingModalEl = document.getElementById("privateRatingModal");
    if (!privateRatingModalEl) return;

    this.privateRatingModal = new bootstrap.Modal(privateRatingModalEl);
    this.privateRatingModalShowing = false;
    privateRatingModalEl.addEventListener("show.bs.modal", () => {
      this.privateRatingModalShowing = true;
    });
    privateRatingModalEl.addEventListener("shown.bs.modal", () => {
      this.privateRatingModalShowing = false;
    });
    document.getElementById("savePublicBtn").addEventListener("click", () => {
      if (!this.pendingFormData) return;
      const formData = this.pendingFormData;
      const action = this.pendingFormAction;
      this.pendingFormData = null;
      this.pendingFormAction = null;
      this.submitBingeForm(formData, action, false);
    });
    document.getElementById("savePrivateBtn").addEventListener("click", () => {
      if (!this.pendingFormData) return;
      const formData = this.pendingFormData;
      const action = this.pendingFormAction;
      this.pendingFormData = null;
      this.pendingFormAction = null;
      this.submitBingeForm(formData, action, true);
    });
  }

  submitBingeForm(formData, action, isPrivate) {
    const csrfToken =
      document.querySelector("meta[name='csrf-token']")?.content ||
      document.querySelector("[name=authenticity_token]")?.value ||
      "";

    if (isPrivate !== null)
      formData.set("private", isPrivate ? "true" : "false");

    this.saveBtn.disabled = true;
    this.saveBtn.textContent = "Saving...";

    this.hidePrivateRatingModal();

    fetch(action, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken },
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          if (this.modal._isShown) {
            this.pendingUpdate = data;
            this.modal.hide();
          } else {
            this.updateCurrentItem(data);
          }
        }
      })
      .catch((error) => console.error("Error:", error))
      .finally(() => {
        this.saveBtn.disabled = false;
        this.saveBtn.innerHTML = '<i class="bi bi-floppy"></i> Save';
      });
  }

  updateCurrentItem(data) {
    if (!this.currentItem) return;

    if (!data.next_episode) {
      this.currentItem.remove();
      this.currentItem = null;
      return;
    }

    const ep = data.next_episode;
    const pad = (n) => String(n).padStart(2, "0");

    this.currentItem.dataset.episodeSlug = ep.slug;
    this.currentItem.dataset.episodeName = ep.name;
    this.currentItem.dataset.seasonNumber = ep.season_number;
    this.currentItem.dataset.episodeNumber = ep.episode_number;
    this.modalEpisodeBadge.innerHTML =
      '<i class="bi bi-collection-play me-1"></i>' +
      "S" +
      pad(ep.season_number) +
      " E" +
      pad(ep.episode_number);
    this.modalEpisodeName.textContent = ep.name;
    this.currentItem.dataset.episodeWatched = "false";
    this.currentItem.dataset.episodeRating = "";

    const badge = this.currentItem.querySelector(".badge.badge-episode");
    if (badge)
      badge.innerHTML =
        '<i class="bi bi-collection-play me-1"></i>' +
        "S" +
        pad(ep.season_number) +
        " E" +
        pad(ep.episode_number);

    const episodeName = this.currentItem.querySelector(
      ".next-item-episode-name",
    );
    if (episodeName) episodeName.textContent = ep.name;

    const airdateDiv = this.currentItem.querySelector(".next-item-airdate");
    if (ep.airdate) {
      if (airdateDiv) {
        airdateDiv.innerHTML =
          '<i class="bi bi-calendar3 me-1"></i>' + ep.airdate;
      } else {
        const episodeDiv = this.currentItem.querySelector(".next-item-episode");
        if (episodeDiv) {
          const newAirdate = document.createElement("div");
          newAirdate.className = "next-item-airdate text-muted";
          newAirdate.innerHTML =
            '<i class="bi bi-calendar3 me-1"></i>' + ep.airdate;
          episodeDiv.after(newAirdate);
        }
      }
    } else if (airdateDiv) {
      airdateDiv.remove();
    }

    const card = this.currentItem;
    this.currentItem = null;
    card.classList.remove("binge-item-updated");
    void card.offsetWidth;
    card.classList.add("binge-item-updated");
    card.addEventListener(
      "animationend",
      () => card.classList.remove("binge-item-updated"),
      { once: true },
    );
  }

  handleSharedWatchCompleted(event) {
    this.updateCurrentItem(event.detail)
  }

  disconnect() {
    document.removeEventListener("shared-watch:completed", this.boundSharedWatchCompleted)
    this.disconnectObserver();
  }

  loadMore() {
    if (this.loading || !this.hasSentinelTarget) return;

    const nextPage = this.sentinelTarget.dataset.nextPage;
    if (!nextPage) return;

    this.loading = true;
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("d-none");

    fetch(this.buildTabUrl(this.activeTab, nextPage), {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then((response) => response.text())
      .then((html) => {
        this.disconnectObserver();

        if (this.hasSpinnerTarget) this.spinnerTarget.remove();
        if (this.hasSentinelTarget) this.sentinelTarget.remove();

        const temp = document.createElement("div");
        temp.innerHTML = html;

        const cards = temp.querySelectorAll(".next-item-card");
        cards.forEach((card) => this.listTarget.appendChild(card));

        if (this.activeTagFilters.size > 0) this.updateFilter();
        const match = html.match(/<!-- next_page:(\d+) -->/);
        if (match) {
          const sentinel = document.createElement("div");
          sentinel.dataset.bingeTarget = "sentinel";
          sentinel.dataset.nextPage = match[1];
          this.resultsTarget.appendChild(sentinel);

          const spinner = document.createElement("div");
          spinner.className = "flow-loading text-center py-4";
          spinner.dataset.bingeTarget = "spinner";
          spinner.innerHTML =
            '<div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>';
          this.resultsTarget.insertBefore(spinner, sentinel);

          this.observeSentinel();
        }

        this.loading = false;
      });
  }

  observeSentinel() {
    this.disconnectObserver();

    if (!this.hasSentinelTarget) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.loadMore();
        });
      },
      { rootMargin: "200px" },
    );

    this.observer.observe(this.sentinelTarget);
  }

  disconnectObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  scrollToTop(event) {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  #loadTags() {
    if (this.tagsLoaded) return;
    this.tagsLoaded = true;

    fetch(this.tagsUrlValue, { headers: { Accept: "text/html" } })
      .then((response) => response.text())
      .then((html) => {
        this.tagsContentTarget.innerHTML = html;
      })
      .catch(() => {
        this.tagsContentTarget.innerHTML =
          '<p class="text-muted">Failed to load.</p>';
      });
  }

  #reloadTags() {
    this.tagsLoaded = false;
    this.tagsContentTarget.innerHTML =
      '<div class="d-flex justify-content-center py-3">' +
      '<div class="spinner-border spinner-border-sm text-secondary" role="status">' +
      '<span class="visually-hidden">Loading...</span>' +
      "</div>" +
      "</div>";
    this.#loadTags();
  }

  createTag() {
    const input = this.newTagInputTarget;
    const name = input.value.trim();
    if (!name) return;

    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]',
    ).content;
    this.tagErrorTarget.classList.add("d-none");

    fetch("/tags", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({ name }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.success) {
          this.tagErrorTarget.textContent =
            data.errors?.join(", ") || "Failed to create tag";
          this.tagErrorTarget.classList.remove("d-none");
          return;
        }
        input.value = "";
        this.#reloadTags();
      });
  }

  startTagEdit(event) {
    const tagId = event.currentTarget.dataset.tagId;
    const row = this.tagsListTarget.querySelector(
      `[data-tag-row="${tagId}"]`,
    );
    row.querySelector("[data-tag-label]").classList.add("d-none");
    const input = row.querySelector("[data-tag-edit-input]");
    input.classList.remove("d-none");
    input.focus();
    row
      .querySelectorAll("[data-tag-view]")
      .forEach((el) => el.classList.add("d-none"));
    row
      .querySelectorAll("[data-tag-edit-btn]")
      .forEach((el) => el.classList.remove("d-none"));
  }

  cancelTagEdit(event) {
    const tagId = event.currentTarget.dataset.tagId;
    const row = this.tagsListTarget.querySelector(
      `[data-tag-row="${tagId}"]`,
    );
    const label = row.querySelector("[data-tag-label]");
    row.querySelector("[data-tag-edit-input]").value =
      label.textContent.trim();
    row.querySelector("[data-tag-edit-input]").classList.add("d-none");
    label.classList.remove("d-none");
    row
      .querySelectorAll("[data-tag-view]")
      .forEach((el) => el.classList.remove("d-none"));
    row
      .querySelectorAll("[data-tag-edit-btn]")
      .forEach((el) => el.classList.add("d-none"));
  }

  saveTagEdit(event) {
    const tagId = event.currentTarget.dataset.tagId;
    const row = this.tagsListTarget.querySelector(
      `[data-tag-row="${tagId}"]`,
    );
    const input = row.querySelector("[data-tag-edit-input]");
    const newName = input.value.trim();
    if (!newName) return;

    const csrfToken = document.querySelector(
      "meta[name='csrf-token']",
    ).content;

    fetch(`/tags/${tagId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({ name: newName }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.success) return;
        const label = row.querySelector("[data-tag-label]");
        const countSpan = label.querySelector("span");
        label.innerHTML = "";
        const icon = document.createElement("i");
        icon.className = "bi bi-tag me-1";
        label.appendChild(icon);
        label.appendChild(document.createTextNode(data.tag.name + " "));
        if (countSpan) label.appendChild(countSpan);
        input.value = data.tag.name;
        const deleteBtn = row.querySelector(
          "[data-action*='confirmTagDelete']",
        );
        if (deleteBtn) deleteBtn.dataset.tagName = data.tag.name;
        input.classList.add("d-none");
        label.classList.remove("d-none");
        row
          .querySelectorAll("[data-tag-view]")
          .forEach((el) => el.classList.remove("d-none"));
        row
          .querySelectorAll("[data-tag-edit-btn]")
          .forEach((el) => el.classList.add("d-none"));
      });
  }

  confirmTagDelete(event) {
    const btn = event.currentTarget;
    this.pendingDeleteTagId = btn.dataset.tagId;
    this.deleteTagNameTarget.textContent = btn.dataset.tagName;
    this.tagDeleteModal.show();
  }

  executeTagDelete() {
    const tagId = this.pendingDeleteTagId;
    if (!tagId) return;

    const csrfToken = document.querySelector(
      "meta[name='csrf-token']",
    ).content;
    const btn = document.getElementById("bingeTagDeleteConfirmBtn");
    btn.disabled = true;

    fetch(`/tags/${tagId}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken },
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.success) return;
        this.pendingDeleteTagId = null;
        this.tagDeleteModal.hide();
        this.#reloadTags();
      })
      .finally(() => {
        btn.disabled = false;
        btn.innerHTML = "<i class='bi bi-trash me-1'></i>Delete";
      });
  }

  updateSharedTagPermission(event) {
    const checkbox = event.currentTarget;
    const sharedTagId = checkbox.dataset.sharedTagId;
    const permission = checkbox.dataset.permission;
    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]',
    ).content;

    fetch(`/shared_tags/${sharedTagId}/permission`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({ [permission]: checkbox.checked }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          checkbox.dataset.saved = "true";
        } else {
          checkbox.checked = !checkbox.checked;
        }
      });
  }

  updateRecipientSharedTagPermission(event) {
    const checkbox = event.currentTarget;
    const sharedTagId = checkbox.dataset.sharedTagId;
    const permission = checkbox.dataset.permission;
    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]',
    ).content;
    const body =
      permission === "status"
        ? { status: checkbox.checked ? "approved" : "pending" }
        : { [permission]: checkbox.checked };

    this.#patchRecipientPermission(
      sharedTagId,
      body,
      csrfToken,
      checkbox,
      (checked) => {
        this.#syncRecipientPermissionSliders(
          sharedTagId,
          permission,
          checked,
          csrfToken,
        );
      },
    );
  }

  #syncRecipientPermissionSliders(
    sharedTagId,
    changedPermission,
    checked,
    csrfToken,
  ) {
    const statusBox = document.getElementById(
      `recipient_status_${sharedTagId}`,
    );
    const watchBox = document.getElementById(
      `recipient_watch_${sharedTagId}`,
    );
    const rateBox = document.getElementById(
      `recipient_rate_${sharedTagId}`,
    );

    if (
      (changedPermission === "recipient_allow_watch" ||
        changedPermission === "recipient_allow_rate") &&
      checked
    ) {
      if (statusBox && !statusBox.checked) {
        statusBox.checked = true;
        this.#patchRecipientPermission(
          sharedTagId,
          { status: "approved" },
          csrfToken,
          statusBox,
        );
      }
    } else if (changedPermission === "status" && !checked) {
      if (watchBox && watchBox.checked) {
        watchBox.checked = false;
        this.#patchRecipientPermission(
          sharedTagId,
          { recipient_allow_watch: false },
          csrfToken,
          watchBox,
        );
      }
      if (rateBox && rateBox.checked) {
        rateBox.checked = false;
        this.#patchRecipientPermission(
          sharedTagId,
          { recipient_allow_rate: false },
          csrfToken,
          rateBox,
        );
      }
    }
  }

  #patchRecipientPermission(sharedTagId, body, csrfToken, checkbox, onSuccess) {
    fetch(`/shared_tags/${sharedTagId}/recipient_permission`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          checkbox.dataset.saved = "true";
          if (onSuccess) onSuccess(checkbox.checked);
        } else {
          checkbox.checked = !checkbox.checked;
        }
      });
  }

  openShareModal(event) {
    this.currentShareBtn = event.currentTarget;
    this.currentShareTagId = this.currentShareBtn.dataset.tagId;
    this.shareTagNameTarget.textContent =
      this.currentShareBtn.dataset.tagName;
    this.shareSearchTarget.value = "";
    this.shareResultsTarget.innerHTML = "";
    this.shareResultsTarget.classList.remove("show");

    const sharedUsers = JSON.parse(
      this.currentShareBtn.dataset.sharedUsers || "[]",
    );
    this.shareCurrentUsersTarget.innerHTML = "";
    sharedUsers.forEach((share) => {
      this.shareCurrentUsersTarget.appendChild(
        this.#buildShareChip(share.id, share.name, share.user_id),
      );
    });

    this.tagShareModal.show();
  }

  closeBingeShareModal() {
    this.tagShareModal.hide();
  }

  searchShareUser(event) {
    const query = event.currentTarget.value.trim();
    if (query.length < 2) {
      this.shareResultsTarget.innerHTML = "";
      this.shareResultsTarget.classList.remove("show");
      return;
    }

    const alreadySharedIds = new Set(
      Array.from(
        this.shareCurrentUsersTarget.querySelectorAll("[data-user-id]"),
      ).map((el) => el.dataset.userId),
    );

    fetch(`/tags/search_users?q=${encodeURIComponent(query)}`)
      .then((response) => response.json())
      .then((data) => {
        const unshared = (data.users || []).filter(
          (u) => !alreadySharedIds.has(String(u.id)),
        );

        if (unshared.length === 0) {
          this.shareResultsTarget.innerHTML =
            '<span class="dropdown-item disabled text-muted small">No friends found</span>';
          this.shareResultsTarget.classList.add("show");
          return;
        }

        this.shareResultsTarget.innerHTML = unshared
          .map(
            (u) =>
              `<button type="button" class="dropdown-item"
                   data-user-id="${u.id}"
                   data-user-name="${u.name}"
                   data-action="click->binge#selectShareUser">${u.name}</button>`,
          )
          .join("");
        this.shareResultsTarget.classList.add("show");
      });
  }

  clearShareSearch() {
    this.shareSearchTarget.value = "";
    this.shareResultsTarget.innerHTML = "";
    this.shareResultsTarget.classList.remove("show");
  }

  selectShareUser(event) {
    const btn = event.currentTarget;
    const userId = btn.dataset.userId;
    const userName = btn.dataset.userName;
    const csrfToken = document.querySelector(
      "meta[name='csrf-token']",
    ).content;

    this.shareResultsTarget.innerHTML = "";
    this.shareResultsTarget.classList.remove("show");
    this.shareSearchTarget.value = "";

    fetch("/shared_tags", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({
        tag_id: this.currentShareTagId,
        shared_with_user_id: userId,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.success) return;
        this.shareCurrentUsersTarget.appendChild(
          this.#buildShareChip(data.shared_tag_id, userName, userId),
        );
        this.#updateSharedWithCell(
          this.currentShareTagId,
          userName,
          "add",
          data.shared_tag_id,
        );
      });
  }

  removeShare(event) {
    const btn = event.currentTarget;
    const sharedTagId = btn.dataset.sharedTagId;
    const userName = btn.dataset.userName;
    const chip = btn.closest("span");
    const csrfToken = document.querySelector(
      "meta[name='csrf-token']",
    ).content;

    fetch(`/shared_tags/${sharedTagId}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken },
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.success) return;
        if (chip) chip.remove();
        this.#updateSharedWithCell(
          this.currentShareTagId,
          userName,
          "remove",
          sharedTagId,
        );
      });
  }

  #buildShareChip(sharedTagId, userName, userId) {
    const chip = document.createElement("span");
    chip.className =
      "badge bg-secondary d-flex align-items-center gap-1";
    chip.dataset.sharedTagId = sharedTagId;
    chip.dataset.userId = userId;
    chip.innerHTML =
      `${userName} ` +
      `<button type="button" class="btn-close btn-close-white" aria-label="Remove" ` +
      `data-shared-tag-id="${sharedTagId}" ` +
      `data-user-name="${userName}" ` +
      `data-action="click->binge#removeShare"></button>`;
    return chip;
  }

  #updateSharedWithCell(tagId, userName, action, sharedTagId) {
    if (!this.hasTagsListTarget) return;
    if (action === "add") {
      const existingSubRows = this.tagsListTarget.querySelectorAll(
        `[data-tag-share-for="${tagId}"]`,
      );
      const tagRow = this.tagsListTarget.querySelector(
        `[data-tag-row="${tagId}"]`,
      );
      if (!tagRow) return;
      const insertAfter = existingSubRows.length
        ? existingSubRows[existingSubRows.length - 1]
        : tagRow;
      const subRow = document.createElement("tr");
      subRow.dataset.tagShareFor = tagId;
      subRow.dataset.sharedTagId = sharedTagId;
      subRow.innerHTML =
        `<td class="ps-4 text-muted small"><i class="bi bi-person me-1"></i>${userName}</td>` +
        `<td class="text-center">` +
        `<div class="d-flex gap-3 justify-content-center align-items-center">` +
        `<div class="form-check form-switch mb-0">` +
        `<input class="form-check-input" type="checkbox" role="switch" ` +
        `id="owner_watch_${sharedTagId}" ` +
        `data-shared-tag-id="${sharedTagId}" ` +
        `data-permission="owner_allow_watch" ` +
        `data-action="change->binge#updateSharedTagPermission">` +
        `<label class="form-check-label small" for="owner_watch_${sharedTagId}">Watch</label>` +
        `</div>` +
        `<div class="form-check form-switch mb-0">` +
        `<input class="form-check-input" type="checkbox" role="switch" ` +
        `id="owner_rate_${sharedTagId}" ` +
        `data-shared-tag-id="${sharedTagId}" ` +
        `data-permission="owner_allow_rate" ` +
        `data-action="change->binge#updateSharedTagPermission">` +
        `<label class="form-check-label small" for="owner_rate_${sharedTagId}">Rate</label>` +
        `</div>` +
        `</div>` +
        `</td>` +
        `<td></td>`;
      insertAfter.insertAdjacentElement("afterend", subRow);
    } else {
      const subRow = this.tagsListTarget.querySelector(
        `[data-tag-share-for="${tagId}"][data-shared-tag-id="${sharedTagId}"]`,
      );
      if (subRow) subRow.remove();
    }
  }

  hidePrivateRatingModal() {
    if (!this.privateRatingModal) return;
    if (this.privateRatingModalShowing) {
      this.privateRatingModal._element.addEventListener(
        "shown.bs.modal",
        () => this.privateRatingModal.hide(),
        { once: true },
      );
    } else {
      this.privateRatingModal.hide();
    }
  }
}
